<script setup lang="ts">
import type { Filter } from '@/types';

// components
import CiFilterBoolean from '@/components/filter/CiFilterBoolean/CiFilterBoolean.vue';
import CiFilterRange from '@/components/filter/CiFilterRange/CiFilterRange.vue';
import CiFilterRating from '@/components/filter/CiFilterRating/CiFilterRating.vue';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFilterGroupProps {
  filter: any;
  isPanel?: boolean;
  selectedFilter: any;
}
const props = withDefaults(defineProps<CiFilterGroupProps>(), {
  isPanel: false,
});

const emit = defineEmits<{
  change: [value: { filter: string; value: boolean | number | number[] }];
}>();

/********************
 * FUNCTIONS         *
 ********************/
function getComponent(filter: Filter) {
  const { name, type, label } = filter;

  const defaultAttributes = {
    value: props.selectedFilter[name],
    label,
    name,
  };

  const defaultEvent = {
    input: (value: boolean | number | number[]) => onValueChange(value, name),
  };

  switch (type) {
    case 'bool':
      if (defaultAttributes.value === undefined) {
        defaultAttributes.value = false;
      }

      return {
        component: CiFilterBoolean,
        attrs: {
          ...defaultAttributes,
          class: props.isPanel ? 'my-2' : 'mb-4',
        },
        events: defaultEvent,
      };
    case 'bitwise':
      if (defaultAttributes.value === undefined) {
        defaultAttributes.value = false;
      }

      return {
        component: CiFilterBoolean,
        attrs: {
          ...defaultAttributes,
          class: props.isPanel ? 'my-2' : 'mb-4',
        },
        events: defaultEvent,
      };
    case 'rating':
      return {
        component: CiFilterRating,
        attrs: {
          ...defaultAttributes,
          displayValue: filter.displayValue,
          inverse: filter.inverse,
          max: filter.max,
          class: props.isPanel ? '' : 'mb-6',
        },
        events: defaultEvent,
      };
    case 'range':
      return {
        component: CiFilterRange,
        attrs: {
          ...defaultAttributes,
          inverse: filter.inverse,
          marker: filter.marker,
          max: filter.max,
          min: filter.min,
          multiplier: filter.multiplier,
          range: filter.range,
          step: filter.step,
          tooltip: filter.tooltip,
          class: 'col-12',
        },
        events: defaultEvent,
      };
    default:
      return {
        component: false,
      };
  }
}

function onValueChange(value: boolean | number | number[], filter: string) {
  emit('change', { value, filter });
}
</script>

<template>
  <div :class="{ 'md:columns-2': !props.isPanel }">
    <template
      v-for="activeFilter in props.filter"
      :key="activeFilter.name"
    >
      <component
        :is="getComponent(activeFilter)?.component"
        v-bind="getComponent(activeFilter)?.attrs"
        v-on="getComponent(activeFilter)?.events"
      />
    </template>
  </div>
</template>

<style></style>
